import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useIsDueno } from "../useIsDueno";
import { ZonaGiroCard } from "./ZonaGiroCard";

interface Freelancer {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  disabled: boolean | null;
}

const nameOf = (f: Freelancer) =>
  [f.first_name, f.last_name].filter(Boolean).join(" ") ||
  f.email ||
  `Freelancer ${f.id}`;

/** Ajustes → Freelancers: el dueño elige a uno y edita su zona y sus ofertas. */
export const FreelancersSection = () => {
  const isDueno = useIsDueno();
  const [selected, setSelected] = useState<Freelancer | null>(null);
  const { data: freelancers, isError } = useQuery({
    queryKey: ["freelancers"],
    enabled: isDueno,
    queryFn: async () => {
      const sb = getSupabaseClient();
      const ents = await sb
        .from("member_entitlements")
        .select("user_id")
        .eq("tier", "publico");
      if (ents.error) throw ents.error;
      const ids = (ents.data ?? []).map((e) => e.user_id as string);
      if (!ids.length) return [];
      const sales = await sb
        .from("sales")
        .select("id, first_name, last_name, email, disabled")
        .in("user_id", ids)
        .order("id");
      if (sales.error) throw sales.error;
      return sales.data as Freelancer[];
    },
  });

  if (!isDueno) return null;

  return (
    <Card className="gap-3 p-4">
      <h2 className="flex items-center gap-2 text-base font-extrabold">
        <Users className="size-5 text-primary" />
        Freelancers
      </h2>
      <p className="text-sm text-muted-foreground">
        Elige un freelancer para cambiar qué estados, municipios y ofertas puede
        usar. Un freelancer nuevo empieza sin zona y sin ofertas.
      </p>
      {isError && (
        <p className="text-sm text-destructive">
          No se pudo cargar la lista de freelancers. Recarga la página.
        </p>
      )}
      {freelancers?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Todavía no hay freelancers dados de alta.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {freelancers?.map((f) => (
          <Button
            key={f.id}
            variant={selected?.id === f.id ? "default" : "outline"}
            onClick={() => setSelected(f)}
          >
            {nameOf(f)}
            {f.disabled && <span className="text-xs">(desactivado)</span>}
          </Button>
        ))}
      </div>
      {selected && (
        <ZonaGiroCard
          key={selected.id}
          salesId={selected.id}
          nombre={nameOf(selected)}
        />
      )}
    </Card>
  );
};
